import { lazy, Suspense, type ReactNode } from 'react';
import { Users, ShoppingBag, Plug, Key } from 'lucide-react';
import type { ShortcutId, ShortcutPersona } from '@evi/shared';
import { SHORTCUT_IDS } from '@evi/shared';
import { MenuIcon, TokenSpin } from '@/components/primitives';

const AnimatedIcon = lazy(() => import('@/components/ui/AnimatedIcon'));

/**
 * Friends, My Chargers and Store have no entry in the animated-WebP
 * menu family; in the Menu's My Charging sheet they are the Lottie
 * icons (/icons/users.json, plug.json, shop.json) behind a Suspense
 * whose fallback is the Lucide glyph — the same shape as ProfilePage's
 * SheetIcon, so a pin shows the icon the driver already knows from
 * the sheet, not a second drawing of the same thing. Loops like the
 * sheet rows; the lottie chunk loads only when a surface with one of
 * these pins mounts.
 */
function LottieIcon({ name, fallback, size = 22 }: { name: string; fallback: ReactNode; size?: number }) {
  return (
    <Suspense fallback={fallback}>
      <AnimatedIcon src={`/icons/${name}.json`} fallback={fallback} size={size} playOnMount playOnTap loop />
    </Suspense>
  );
}

/**
 * The home-screen shortcut catalog (frictionless flow, piece 1).
 *
 * One entry per ShortcutId: the i18n label key (shortcuts.json), the
 * icon at the FAB chip size, and where it goes. Targets that need the
 * driver's own charger read `home_charger_id` from /auth/me — "Start my
 * charger" lands on that charger's page (the owner start lives there
 * for every charger the driver owns, at a home or a building) and
 * "Earnings" on its earnings page. Without an owned charger: a
 * homeowner (persona from a Home tenant) is sent to My Home, where
 * registering one begins; anyone else to My Chargers.
 *
 * `personas` gates the PICKER only: a pure driver is not offered
 * "Earnings" (they have none), but a pin that stops matching (a
 * homeowner who deletes the charger) still renders — its target
 * degrades to My Chargers rather than vanishing from under them.
 */
export interface ShortcutDef {
  id: ShortcutId;
  labelKey: string;
  /** Icon at chip / tile size (fits a 28–36px slot). */
  icon: ReactNode;
  /** Optional larger icon for the map FAB stack, where one chip may
   *  carry a hero (the spinning token); falls back to `icon`. */
  fabIcon?: ReactNode;
  to: (ctx: { homeChargerId: string | null; persona: ShortcutPersona }) => string;
  personas: ReadonlyArray<ShortcutPersona>;
}

const ALL: ReadonlyArray<ShortcutPersona> = ['homeowner', 'fleet', 'driver'];
const HOME: ReadonlyArray<ShortcutPersona> = ['homeowner'];

export const SHORTCUT_CATALOG: Record<ShortcutId, ShortcutDef> = {
  find_charger: { id: 'find_charger', labelKey: 'items.find_charger', icon: <MenuIcon name="pin" size={18} alt="" />, to: () => '/map', personas: ALL },
  start_charger: { id: 'start_charger', labelKey: 'items.start_charger', icon: <MenuIcon name="charging" size={18} alt="" />, to: () => '/scan', personas: ALL },
  start_my_charger: {
    id: 'start_my_charger', labelKey: 'items.start_my_charger', icon: <MenuIcon name="my-home" size={18} alt="" />,
    to: ({ homeChargerId, persona }) => (
      homeChargerId ? `/my-chargers/${homeChargerId}` : persona === 'homeowner' ? '/my-home' : '/my-chargers'
    ),
    personas: HOME,
  },
  earnings: {
    id: 'earnings', labelKey: 'items.earnings', icon: <MenuIcon name="payouts" size={18} alt="" />,
    to: ({ homeChargerId }) => (homeChargerId ? `/my-chargers/${homeChargerId}/earnings` : '/my-chargers'), personas: HOME,
  },
  // On the FAB the token is sized up so the spin reads as the visual
  // anchor of the stack, as it did on the old hardcoded chip; tiles and
  // the Menu row get it at slot size.
  rewards: { id: 'rewards', labelKey: 'items.rewards', icon: <TokenSpin size={22} />, fabIcon: <TokenSpin size={44} />, to: () => '/rewards', personas: ALL },
  friends: { id: 'friends', labelKey: 'items.friends', icon: <LottieIcon name="users" fallback={<Users size={18} />} />, to: () => '/friends', personas: ALL },
  my_vehicle: { id: 'my_vehicle', labelKey: 'items.my_vehicle', icon: <MenuIcon name="my-ev" size={18} alt="" />, to: () => '/vehicle', personas: ALL },
  sessions: { id: 'sessions', labelKey: 'items.sessions', icon: <MenuIcon name="history" size={18} alt="" />, to: () => '/history', personas: ALL },
  coupons: { id: 'coupons', labelKey: 'items.coupons', icon: <MenuIcon name="ticket" size={18} alt="" />, to: () => '/coupons', personas: ALL },
  favorites: { id: 'favorites', labelKey: 'items.favorites', icon: <MenuIcon name="heart" size={18} alt="" />, to: () => '/favorites', personas: ALL },
  store: { id: 'store', labelKey: 'items.store', icon: <LottieIcon name="shop" fallback={<ShoppingBag size={18} />} />, to: () => '/store', personas: ALL },
  wallet: { id: 'wallet', labelKey: 'items.wallet', icon: <MenuIcon name="wallet" size={18} alt="" />, to: () => '/wallet', personas: ALL },
  my_home: { id: 'my_home', labelKey: 'items.my_home', icon: <MenuIcon name="my-home" size={18} alt="" />, to: () => '/my-home', personas: HOME },
  my_chargers: { id: 'my_chargers', labelKey: 'items.my_chargers', icon: <LottieIcon name="plug" fallback={<Plug size={18} />} />, to: () => '/my-chargers', personas: HOME },
  third_party_chargers: {
    id: 'third_party_chargers', labelKey: 'items.third_party_chargers',
    icon: <LottieIcon name="key" fallback={<Key size={18} />} />,
    // This is navigation only. /my-access and session start both enforce
    // current grants server-side, including after a saved pin is revoked.
    to: () => '/my-access', personas: ALL,
  },
};

/** Catalog entries the picker offers a persona, in catalog order. */
export function shortcutsOfferedTo(persona: ShortcutPersona): ShortcutDef[] {
  return SHORTCUT_IDS.map((id) => SHORTCUT_CATALOG[id]).filter((d) => d.personas.includes(persona));
}
